import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaTimes } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { formatMoney } from '../../components/Utils/FormatUtils';

/**
 * « Demandes de mon équipe à valider » (Yros/Kavero#468).
 *
 * Le serveur ne rend que les demandes des N-1 du manager qui attendent sa
 * décision (`_yros_rh_equipe()`). Deux boutons explicites par demande ;
 * refuser demande un motif, que le salarié lira. Une demande décidée quitte
 * la liste. Rien à valider : la section se tait, pour ne pas poser un bloc
 * vide au-dessus de l'équipe.
 */

// Le message du serveur, ou la clé d'un texte générique, traduite au rendu :
// le chargement ne tourne qu'une fois.
const message = (e, cle) => e?.response?.data?.message || cle;
const TEXTES = ['demandesEquipe.erreur', 'demandesEquipe.erreurDecision'];

function Objet({ demande }) {
  const { i18n } = useTranslation();
  const contenu = demande.contenu || {};
  const jour = (iso) => new Intl.DateTimeFormat(i18n?.language || 'fr-FR',
    { day: '2-digit', month: '2-digit' }).format(new Date(`${iso}T00:00:00`));
  const details = [];
  if (contenu.date_debut && contenu.date_fin) details.push(`${jour(contenu.date_debut)} → ${jour(contenu.date_fin)}`);
  if (contenu.date) details.push(`${jour(contenu.date)} ${contenu.heure_debut || ''}–${contenu.heure_fin || ''}`);
  if (typeof contenu.montant === 'number') details.push(formatMoney(contenu.montant));
  return (
    <p className="text-sm text-slate-600">
      <span className="font-medium text-slate-700">{contenu.libelle}</span>
      {details.length > 0 && ` · ${details.join(' · ')}`}
    </p>
  );
}

function DemandesEquipe({ onCompte, demandeurId = null }) {
  const { t } = useTranslation();
  const [demandes, setDemandes] = useState(null);
  // Yros/Kavero#488 : « Mon équipe » affiche le nombre de demandes en attente
  // sur son onglet et dans la navigation.
  const signaler = useRef(onCompte);
  signaler.current = onCompte;
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(null);
  const [refusPour, setRefusPour] = useState(null);
  const [motif, setMotif] = useState('');

  useEffect(() => {
    let actif = true;
    apiService.lireDemandesEquipe()
      .then((reponse) => {
        if (!actif) return;
        if (reponse?.ok) setDemandes(reponse.demandes || []);
        else setErreur(reponse?.message || 'demandesEquipe.erreur');
      })
      .catch(() => { if (actif) setErreur('demandesEquipe.erreur'); });
    return () => { actif = false; };
  }, []);

  useEffect(() => {
    if (demandes !== null && signaler.current) signaler.current(demandes.length, demandes);
  }, [demandes]);

  const cle = (demande) => `${demande.type}-${demande.id}`;

  const decider = async (demande, approuver, motifSaisi = null) => {
    setErreur(null);
    setEnvoi(cle(demande));
    try {
      const reponse = await apiService.deciderDemandeEquipe({
        type: demande.type, id: demande.id, approuver, motif: motifSaisi,
      });
      if (reponse?.ok) {
        setDemandes((liste) => liste.filter((d) => cle(d) !== cle(demande)));
        setRefusPour(null);
        setMotif('');
      } else {
        setErreur(reponse?.message || 'demandesEquipe.erreurDecision');
      }
    } catch (e) {
      setErreur(message(e, 'demandesEquipe.erreurDecision'));
    } finally {
      setEnvoi(null);
    }
  };

  // Depuis la fiche d'un N-1 (#488) : seulement les siennes.
  const visibles = demandes && demandeurId !== null
    ? demandes.filter((demande) => demande.demandeur?.id === demandeurId)
    : demandes;
  if (!erreur && (!visibles || visibles.length === 0)) return null;

  return (
    <section className="space-y-3">
      <h3 className="text-base font-semibold text-slate-800">
        {t('demandesEquipe.titre', { count: visibles?.length || 0 })}
      </h3>
      {erreur && (
        <p data-testid="equipe-demandes-erreur" role="alert"
          className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {TEXTES.includes(erreur) ? t(erreur) : erreur}
        </p>
      )}
      {visibles && visibles.length > 0 && (
        <ul className="space-y-2">
          {visibles.map((demande) => (
            <li key={cle(demande)} data-testid={`equipe-demande-${cle(demande)}`}
              className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 space-y-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">{demande.demandeur?.nom}</p>
                  <Objet demande={demande} />
                </div>
                <span className="text-xs text-slate-500">
                  {t(`circuitsValidation.types.${demande.type}`)}
                </span>
              </div>

              {refusPour === cle(demande) ? (
                <div className="space-y-2">
                  <textarea
                    data-testid="equipe-motif"
                    value={motif}
                    onChange={(e) => setMotif(e.target.value)}
                    placeholder={t('demandesEquipe.motifPlaceholder')}
                    aria-label={t('demandesEquipe.motifTitre')}
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg text-sm text-slate-700 bg-slate-50"
                  />
                  <div className="flex justify-end gap-2">
                    <button type="button" data-testid="equipe-refus-annuler"
                      onClick={() => { setRefusPour(null); setMotif(''); }}
                      className="px-4 py-2 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors">
                      {t('demandesEquipe.annuler')}
                    </button>
                    <button type="button" data-testid="equipe-refus-confirmer"
                      disabled={!motif.trim() || envoi !== null}
                      onClick={() => decider(demande, false, motif.trim())}
                      className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                      {t('demandesEquipe.confirmerRefus')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-end gap-2">
                  <button type="button" data-testid={`equipe-refuser-${cle(demande)}`}
                    disabled={envoi !== null}
                    onClick={() => { setRefusPour(cle(demande)); setMotif(''); }}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors">
                    <FaTimes aria-hidden="true" />
                    {t('demandesEquipe.refuser')}
                  </button>
                  <button type="button" data-testid={`equipe-approuver-${cle(demande)}`}
                    disabled={envoi !== null}
                    onClick={() => decider(demande, true)}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-[--color-primary] text-white hover:bg-[--bg-hover] disabled:opacity-50 transition-colors">
                    <FaCheck aria-hidden="true" />
                    {t('demandesEquipe.approuver')}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default DemandesEquipe;
